import React, { useCallback, useState } from 'react'
import Router from 'next/router'
import Link from 'next/link'
import toast from 'react-hot-toast'
import { ExhibitStatus, Exhibition, Feature } from '@prisma/client'
import { useForm } from 'react-hook-form'
import { useSession } from 'next-auth/react'
import {
  createExhibition, updateExhibition, updateExhibitionQuietMode, deleteExhibition, useTenantFeatures
} from '@src/lib/apiClient'
import { getQuillPlaintext, titleToFilename } from '@src/lib/quill'
import { DownloadableQRCode, getOrigin } from './DownloadableQRCode'
import { ExhibitionWorkLabelHelper, ExhibitionStatusHelp, ExhibitionPublishedHelp } from './popovers/index'

type ExhibitionFormProps = {
  exhibition?: Exhibition;
  mutateExhibition?: () => void;
}

export const ExhibitionForm: React.FC<ExhibitionFormProps> = ({
  exhibition, mutateExhibition,
}: ExhibitionFormProps) => {
  const {
    register, handleSubmit, formState: { isValid, isSubmitting, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      ...exhibition,
      workLabel: exhibition?.workLabel || 'work',
    },
  })
  const { data: features, isLoading } = useTenantFeatures()
  const { data: session, status } = useSession()
  const sessionLoading = status === 'loading'

  const handleExhibitionQuietMode = useCallback((e) => {
    const newVal: number = !Number.isNaN(Number(e.target.value)) ? Math.min(Math.max(Number(e.target.value), 0), 1) : NaN
    if (!Number.isNaN(newVal) && exhibition) {
      updateExhibitionQuietMode(exhibition.id, newVal)
    }
  }, [exhibition])

  const [showQuietModeControl, setShowQuietModeControl] = useState(false)

  const submitData = async (data: Exhibition) => {
    if (!data.showStatus) data.showStatus = ExhibitStatus.Upcoming
    if (exhibition) {
      const { error } = await updateExhibition(exhibition.id, data)
      if (error) {
        toast.error(error.message)
      } else {
        mutateExhibition()
        toast.success('Exhibition updated.')
        await Router.push('/admin')
      }
    } else {
      const { error } = await createExhibition(data)
      if (error) {
        toast.error(error.message)
      } else {
        toast.success('Exhibition created.')
        await Router.push('/admin')
      }
    }
  }
  const handleClickDelete = async () => {
    if (!window.confirm('Are you sure you want delete this exhibition and all related works?')) {
      return
    }
    const { error } = await deleteExhibition(exhibition.id)
    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Exhibition deleted.')
      await Router.push('/admin')
    }
  }
  if (isLoading) {
    return (
      <p>Loading...</p>
    )
  }
  return (
    <div>
      <form onSubmit={handleSubmit(submitData)}>
        <div className="form-header">
          <div className="form-header__left">
            <h1>
              {`${!exhibition ? 'New ' : ''}Exhibition${exhibition ? ' Settings' : ''}`}
            </h1>
            {exhibition && (<a href={`/exhibition/${exhibition.id}?includeInactive=true`} target="_blank" rel="noreferrer">Guest View</a>)}
          </div>
          <a href="https://docs.accesskit.cc/reference/exhibition" target="_blank" rel="noreferrer">Help</a>
        </div>
        <label htmlFor="name">
          Exhibition Name
          <input
            placeholder="Dave McKenzie"
            type="text"
            {...register('name', { required: 'Exhibition name is required.' })}
          />
          <p>{errors.name?.message}</p>
        </label>
        <label htmlFor="heroImageUrl">
          Hero Image URL
          <input
            placeholder="https://example.org/guide/98.jpg"
            type="text"
            {...register('heroImageUrl')}
          />
          <p>{errors.heroImageUrl?.message}</p>
        </label>
        <label htmlFor="heroImageAltText">
          Hero Image Alt Text
          <input
            placeholder="A close-up of orange leaves, brightly lit in the center, glowing yellow."
            type="text"
            {...register('heroImageAltText')}
          />
          <p>{errors.heroImageAltText?.message}</p>
        </label>
        <label htmlFor="secondaryImageUrl">
          Secondary Image URL
          <input
            placeholder="https://example.org/guide/98.jpg"
            type="text"
            {...register('secondaryImageUrl')}
          />
          <p>{errors.secondaryImageUrl?.message}</p>
        </label>
        <label htmlFor="secondaryImageAltText">
          Secondary Image Alt Text
          <input
            placeholder="Another close-up of orange leaves, brightly lit in the center, glowing yellow."
            type="text"
            {...register('secondaryImageAltText')}
          />
          <p>{errors.secondaryImageAltText?.message}</p>
        </label>
        <label htmlFor="workLabel">
          Work Label
          <ExhibitionWorkLabelHelper />
          <input
            placeholder="work"
            type="text"
            {...register('workLabel')}
          />
          <p>{errors.workLabel?.message}</p>
        </label>
        {exhibition && (showQuietModeControl
          ? (
            <label htmlFor="quietMode">
              Quiet Mode Factor (0.00-1.00)
              {!features.includes(Feature.QUIET_MODE) && <em> Not included in current plan.</em>}
              <input
                disabled={!features.includes(Feature.QUIET_MODE)}
                placeholder="0.5"
                type="number"
                min={0}
                max={1}
                step={0.01}
                onChange={handleExhibitionQuietMode}
              />
            </label>
          ) : (
            <button
              disabled={!features.includes(Feature.QUIET_MODE)}
              type="button"
              className="quietmode-button"
              onClick={() => setShowQuietModeControl(true)}
            >
              use quiet mode
              {!features.includes(Feature.QUIET_MODE) && <em> Not included in current plan.</em>}
            </button>
          ))}
        <div className="space-y-2">
          <p className="font-medium">
            Exhibition Status
            <ExhibitionStatusHelp />
          </p>
          <div className="space-y-2">
            <label htmlFor="showStatusUpcoming" className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                value="Upcoming"
                {...register('showStatus')}
                id="showStatusUpcoming"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span>Upcoming</span>
            </label>
            <label htmlFor="showStatusCurrent" className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                value="Current"
                {...register('showStatus')}
                id="showStatusCurrent"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span>Current</span>
            </label>
            <label htmlFor="showStatusArchived" className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                value="Archived"
                {...register('showStatus')}
                id="showStatusArchived"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span>Archived</span>
            </label>
          </div>
        </div>
        <label htmlFor="isActive" className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('isActive')}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span>Published?</span>
          <ExhibitionPublishedHelp />
        </label>
        <div className="form-submit">
          <input type="submit" value={`${exhibition ? 'Update' : 'Create'} Exhibition`} disabled={!isValid || isSubmitting} />
          {exhibition && <button className="delete" type="button" onClick={handleClickDelete}>Delete Exhibition</button>}
          <Link href="/admin">
            or Cancel
          </Link>
        </div>
      </form>
      {
        exhibition && (
          <>
            <h5>Read-Only Data</h5>
            <p>ID:</p>
            <p>
              <span style={{ fontFamily: 'monospace' }}>{exhibition.id}</span>
              {' '}
              <a href={`/exhibition/${exhibition.id}`}>Guest View</a>
            </p>
            <p>QR Code:</p>
            <DownloadableQRCode
              id={exhibition.id}
              title={titleToFilename(getQuillPlaintext(exhibition.name))}
              value={`${getOrigin(session && !sessionLoading && session.user.tenantData?.tenantAlias)}/exhibition/${exhibition.id}`}
            />
          </>
        )
      }
    </div>
  )
}
